import { ReactComponent as IconRefresh } from "@/assets/svgs/refresh.svg";
import { ToggleSwitch } from "@/components/ToggleSwitch2/index";
import useModal from "@/hooks/use-modal/index";
import useToast from "@/hooks/use-toast";
import { settingApi } from "@/services/api/setting.js";
import { Fragment, useEffect, useMemo, useState } from "react";
import { ResetNotificationModal } from "../modals/reset-notification-modal.jsx";
import Button from "@/componentsDS/Button/Button";
import { ButtonSize, ButtonType, ButtonVariant } from "@/componentsDS/Button/Button.type.js";
import Modal from "@/dsComponents/Modal";
import { ModalBody, ModalFooter, ModalFooterLeft, ModalFooterRight, ModalHeader, ModalTitle } from "@dextech-ai/dex-design-system";
import { NotificationsOutlined } from "@dextech-ai/dex-design-system/icons";
import useDataElements from "@/hooks/use-data-elements/index.js";
import useUserDataElement from "@/hooks/use-user-data-element/index.js";
import API from "@/services/api/userDataElement.js";
import { ModifyUserDataElementModal } from "../modals/modify-user-data-element-modal.jsx";
import { useBuildex } from '@/hooks/use-buildex';
import InputDropdownDS from '@/dsComponents/select/InputDropdownSelect/index'
import { useFeature } from "@/hooks/use-feature/index.js";
import { featureConstant } from "@/constants/configuration/feature.js";
import { useTradex } from "@/hooks/use-tradex";
import { useHealthdex } from "@/hooks/use-healthdex";
import { Box } from "@dextech-ai/dex-design-system";


export const filterSelectedDataElements = (dataElements, userDataElements, isEmailPreferenceEnabled) => {
	let selectedDE = []
	if(!userDataElements) return selectedDE
	if (isEmailPreferenceEnabled) {
		selectedDE = dataElements.filter((dataElement) => userDataElements.includes(dataElement.id))
		return selectedDE
	} else {
		 selectedDE = userDataElements.map((item) => {
			const dataElement = dataElements.find((dataElement) => dataElement.id === item.dataElementId);
			return dataElement;
		});
		return selectedDE
	}

}

export const updateDERequestPayload = (notificationSettings,dataElementIds) => {

	const buildItemPayload = (item) => ({
		emailNotificationId: item.id,
		userNotificationEnabled: item.checked,
		dataElementIds,
	});

	return notificationSettings
	  .flatMap(group =>
		group.subgroups.flatMap(subgroup =>
		  subgroup.items.map(buildItemPayload))
	  );
  };

export function getChangedNotifications(newState, groupIdx, targetValue) {
	const group = newState[groupIdx];
	group.checked = targetValue;

	const requestPayload = [];
	group.subgroups.forEach((subgroup) =>
		subgroup.items.forEach((item) => {
			if (item.checked === targetValue) {
				return;
			}
			item.checked = targetValue;
			requestPayload.push({
				emailNotificationId: item.id,
				userNotificationEnabled: targetValue,
			});
		})
	);
	return requestPayload;
}
export function getNotificationMessage(userDataElementItems, dataElementItems) {
	const userLen = userDataElementItems?.length || 0;
	const dataLen = dataElementItems?.length || 0;
	if (!userLen || !dataLen) return 'Receiving notifications for 0 data element(s)';
	if (userLen !== dataLen) return `Receiving notifications for ${userLen} data element(s)`;
	return 'Receiving notifications for all data elements';
}

export const NotificationSetting = ({ setLoading }) => {
	const { addToastMessage } = useToast();
	const isEmailPreferenceEnabled = useFeature(featureConstant.EMAIL_PREFERENCE)

	const {
		visible: isModalVisible,
		show: showResetModal,
		hide: hideResetModal,
	} = useModal();

	const dailyReportKeyWord = "Daily Reports";

	const [notificationSettings, setNotificationSettings] = useState([]);
	const [enabledDataElements, setEnabledDataElements] = useState([]);

	const [chipValues, setChipValues] = useState([]);

	const { fetch: fetchDataElement, items: dataElementItems } = useDataElements();
	const { fetch: fetchUserDataElement, items: userDataElementItems } = useUserDataElement();

	/*=================================================
		MultiDex
	/================================================*/
	const isBuildex = useBuildex();
	const isTradex = useTradex();
	const isHealthdex = useHealthdex();

	function formatter(response) {
		try {
			response.sort((a, b) => {
				if (a.sequenceNo > b.sequenceNo) {
					return 1;
				}
				return -1;
			});
			const groups = response.reduce((prev, curr) => {
				// Group
				if (!prev.find((g) => g.description === curr.group)) {
					prev.push({
						description: curr.group,
						checked: curr.notificationEnabled,
						subgroups: [],
					});
				}

				const group = prev.find((g) => g.description === curr.group);

				// Deduce the value of group checkbox
				group.checked = curr.notificationEnabled || group.checked;

				if (curr.group === 'Email Notifications') {
					curr.subGroup = (curr.id === 'ADMIN_DATAEXCH_REMOVE_SYSTEM' && isTradex) ? 'Pitstop' : 'Data Element';
				}
				// Sub group
				if (
					!group.subgroups.find(
						(sg) => sg.description === curr.subGroup
					)
				) {
					group.subgroups.push({
						description: curr.subGroup,
						items: [],
					});
				}

				const subGroup = group.subgroups.find(
					(sg) => sg.description === curr.subGroup
				);

				// Items
				if (isTradex || ((isBuildex || isHealthdex) && curr.id !== 'ADMIN_DATAEXCH_REMOVE_SYSTEM')) {
					subGroup.items.push({
						id: curr.id,
						description: curr.description,
						checked: curr.notificationEnabled,
						sequenceNo: curr.sequenceNo,
					});
				}
				return prev;
			}, []);
			return groups;
		} catch (error) {
			addToastMessage("Failed to format notification response", "error");
			return [];
		}
	}

	async function getNotifications(mounted = true) {
		const [error, response] = await settingApi.getNotifications({
			setLoading,
			addToastMessage,
		});

		const filterResponse = (isBuildex || isHealthdex) ? response.filter(item => item.id !== 'ADMIN_DATAEXCH_REMOVE_SYSTEM') : response;
		if (!mounted) {
			return;
		}
		if (error) {
			setNotificationSettings([]);
			return;
		}

		const formattedData = formatter(filterResponse);
		if(isEmailPreferenceEnabled  ){
			const dataElementIds = filterResponse.find(item =>item.dataElementIds)?.dataElementIds;
			if(dataElementIds){
				setEnabledDataElements(dataElementIds)
			}
		}
		setNotificationSettings(formattedData);
	}

	async function updateNotifications(
		requestPayload,
		newState,
		mounted = true
	) {
		const [error] = await settingApi.updateNotifications(requestPayload, {
			setLoading,
			addToastMessage,
		});

		if (!mounted || error) {
			return;
		}
		setNotificationSettings(newState);
	}

	useEffect(() => {
		let mounted = true;
		getNotifications(mounted);
		fetchDataElement();
		fetchUserDataElement();

		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		if (userDataElementItems && dataElementItems) {
			const dataElements = dataElementItems.map(item => ({  label: item.label ?? item.name , id: item.id}))
			const selectedDataElements = filterSelectedDataElements(dataElements, isEmailPreferenceEnabled ? enabledDataElements : userDataElementItems, isEmailPreferenceEnabled)
			setChipValues(selectedDataElements);
		}

	}, [userDataElementItems, dataElementItems, enabledDataElements]);

	function cloneState(state) {
		return JSON.parse(JSON.stringify(state));
	}

	async function onSingleNotificationChanged(groupIdx, subgroupIdx, itemIdx) {
		const newState = cloneState(notificationSettings);
		const item = newState[groupIdx].subgroups[subgroupIdx].items[itemIdx];
		item.checked = !item.checked;

		const hasCheckedItem = newState[groupIdx].subgroups.reduce(
			(prev, curr) => {
				return prev || !!curr.items.find((itm) => itm.checked);
			},
			false
		);

		newState[groupIdx].checked = hasCheckedItem;

		const requestPayload = [
			{
				emailNotificationId: item.id,
				userNotificationEnabled: item.checked,
				dataElementIds:enabledDataElements
			},
		];
		await updateNotifications(requestPayload, newState);
	}

	async function onMultiNotificationChanged(groupIdx) {
		const newState = cloneState(notificationSettings);
		const requestPayload = getChangedNotifications(
			newState,
			groupIdx,
			!newState[groupIdx].checked
		);

		const updatedPayload = requestPayload.map((item ) => ({...item, dataElementIds:enabledDataElements}))


		await updateNotifications(updatedPayload, newState);
	}

	function onResetBtnClicked() {
		showResetModal();
	}

	async function onResetModalConfirm() {
		const newState = cloneState(notificationSettings);
		const defaultValue = true;
		const requestPayload = newState.reduce(
			(prev, curr, currIdx, callerObj) => {
				const payload = getChangedNotifications(
					callerObj,
					currIdx,
					defaultValue
				);
				return [...prev, ...payload];
			},
			[]
		);

		await updateNotifications(requestPayload, newState);
	}

	/*=================================================
		Modal
	/================================================*/
	const { visible: visibleDataElementNotification, show: showDataElementNotification, hide: hideDataElementNotification } = useModal()

	function onModifyBtnClick() {
		showDataElementNotification()
	}

	/*=================================================
		Modal Input
	/================================================*/
	const transformedDataElements = useMemo(() => {
		return dataElementItems.map((item) => ({
		  ...item,
		  label: item.name,
		  uncategorized: 'uncategorized',
		}));
	}, [dataElementItems]);

	// const filteredDataElements = useMemo(() => {
	// 	return transformedDataElements.filter((item) =>
	// 		item.label.toLowerCase().includes(inputValue.toLowerCase())
	// 	);
	// }, [inputValue, transformedDataElements]);

	// Dropdown search Handler
	const [dropdownSearch, setDropdownSearch] = useState({
		dataElementSearch: '',
	})

	const dropdownSearchHandler = (e) => {
		if (e?.target) { setDropdownSearch(prev => ({ ...prev, [e.target]: e.target.value })); }
	}




	async function handleConfirm() {
		const selectedDataElementIds = {"dataElementIds": chipValues.map((item) => item.id)};

		if(isEmailPreferenceEnabled){
			const requestPayload = updateDERequestPayload(notificationSettings,selectedDataElementIds.dataElementIds);
			await updateNotifications(requestPayload, notificationSettings, false);
		} else {

			const [error] = await API.create(selectedDataElementIds);

			if (error) {
				addToastMessage("Failed to update user data element", "error");
				return;
			}
		}


		fetchDataElement();
		getNotifications(true);
		fetchUserDataElement();
		hideDataElementNotification();
	}

	return (
		<>
			<ResetNotificationModal
				isModalVisible={isModalVisible}
				hideModal={hideResetModal}
				confirmCallback={onResetModalConfirm}
			/>
			<ModifyUserDataElementModal/>
			{/* Full-width anchor row: the reset control lines up with the tab bar
			    without overlapping it. */}
			<div className="account-settings__reset-row">
					<div
						data-testid="reset-default-notification-btn"
						className="reset-default-notification-btn"
						onClick={() => onResetBtnClicked()}
					>
						<div className="flex-box flex-align-center">
							<IconRefresh />
							<div className="reset-default-notification-btn-text font-weight-bold">
								Reset to default
							</div>
						</div>
					</div>
			</div>
			<div className="notification-container">

				<div className="notification-note">
					<span className="font-weight-bold text-color-purple">
						Note:{" "}
					</span>
					<span className="font-weight-normal">
						All changes are automatically saved.
					</span>
				</div>
				{notificationSettings?.map((group, groupIdx) => {
					return (
						<Fragment key={groupIdx}>
							<div
								data-testid="group-container"
								className="group-container"
							>
								<div className="flex-box mt-4 px-1">
									<div className="group-title font-weight-bold flex-10">
										{group.description}
									</div>
									<Box flex={2}>
										<ToggleSwitch
											data-testid="group-toggle"
											item={group}
											labelText={
												group.checked ? "On" : "Off"
											}
											callback={() =>
												onMultiNotificationChanged(
													groupIdx
												)
											}
										/>
									</Box>
								</div>
								{group.description === dailyReportKeyWord && (
									<div
										data-testid="extra-description"
										className="font-size-s font-color-grey-80 mt-4 px-1 font-weight-normal"
									>
										Daily, 05:00 PM, Singapore &#40;SGT&#41;
									</div>
								)}

								<div className="group-content">
									{group.subgroups?.map(
										(subgroup, subgroupIdx) => {
											return (
												<div key={subgroupIdx}>
													{group.subgroups.length > 1 && subgroupIdx == group.subgroups.length - 1 && (
														<hr className="separation-line" />
													)}
													{!!subgroup.description && (
														<div className="flex-box mt-4 px-1">
															<Box flex={10}>
																<div className="font-weight-bold">
																	{subgroup.description}
																</div>
																<div>
																	{subgroup.description === 'Data Element' && (
																		<div className="font-color-grey-80 font-size-s line-height-2 mb-3">
																			{getNotificationMessage(isEmailPreferenceEnabled? enabledDataElements:userDataElementItems, dataElementItems)}
																		</div>
																	)}
																</div>
															</Box>
															<Box flex={2}>
																{(isBuildex || isHealthdex) && subgroup.description === 'Data Element' && (
																	<div className="flex-box flex-end">
																		<Button
																			text="Modify"
																			type={ButtonType.GHOST}
																			variant={ButtonVariant.STANDARD}
																			size={ButtonSize.SMALL}
																			onClick={() => onModifyBtnClick()}
																		/>
																	</div>
																)}
															</Box>
														</div>
													)}
													{subgroup.items?.map(
														(item, itemIdx) => {
															return (
																<div
																	key={
																		item.id
																	}
																	className={`notification-item-container flex-box flex-align-center p-1 ${
																		item.id !== "ADMIN_DAILY_REPORT" ? "" : "notification-item-container-no-hover"
																	}`}
																>
																	<div className="notification-item font-weight-normal flex-10">
																		<div className="flex-box flex-direction-column">
																			<div>
																				{
																					item.description
																				}
																			</div>

																			{[
																				"ADMIN_DATAEXCH_APPROVE",
																				"ADMIN_DATAEXCH_REJECT",
																				"ADMIN_DATAEXCH_REVOKE_ACCESS",
																				"ADMIN_DATAEXCH_REQUEST",
																				"ADMIN_DATAEXCH_CANCEL",
																			].includes(
																				item.id
																			) && (
																				<div className="font-color-grey-80 font-size-s line-height-2">
																					Contribute, Provide On Behalf, Receive On Behalf, Consume
																				</div>
																			)}

																			{["ADMIN_DATAEXCH_REMOVE_SYSTEM"].includes(
																				item.id
																			) && (
																				<div className="font-color-grey-80 font-size-s line-height-2">
																					My Pitstop is deleted by Contributor / Consumer
																				</div>
																			)}
																		</div>
																	</div>

																	<Box flex={2}>
																		{item.id !== "ADMIN_DAILY_REPORT" ? (
																			<ToggleSwitch
																				data-testid="item-toggle"
																				item={
																					item
																				}
																				labelText={ item.checked ? "On" : "Off" }
																				callback={() =>
																					onSingleNotificationChanged(
																						groupIdx,
																						subgroupIdx,
																						itemIdx
																					)
																				}
																			/>
																		) : (
																			<div></div>
																		)}
																	</Box>
																</div>
															);
														}
													)}
												</div>
											);
										}
									)}
								</div>
							</div>
							{groupIdx !== notificationSettings.length - 1 && (
								<hr className="separation-line" />
							)}
						</Fragment>
					);
				})}
			</div>

			<Modal visible={visibleDataElementNotification} close={hideDataElementNotification} dismissClickOutsideModal={false}>
				<ModalHeader icon={<NotificationsOutlined />}>
					<ModalTitle>Email Notifications - Data Element</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<div className="notification-modal">
						<div className="notification-modal__top">
							Receive notification for the following Data Elements.
						</div>
						<div className="notification-modal__bottom">
							<InputDropdownDS
								value={dropdownSearch.dataElementSearch}
								onChange={dropdownSearchHandler}
								name={'dataElementSearch'}
								dropdownOption={transformedDataElements}
								placeholder={"Select an option..."}
								onSelectDropdownItem={setChipValues}
								dropdownSelectedOption={chipValues}
								multipleSelection={true}
							/>
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalFooterLeft>
						<Button
							text="Cancel"
							type={ButtonType.GHOST}
							variant={ButtonVariant.STANDARD}
							size={ButtonSize.SMALL}
							onClick={() => {
								fetchDataElement();
								fetchUserDataElement();
								hideDataElementNotification()
							}}
						/>
					</ModalFooterLeft>
					<ModalFooterRight>
						<Button
							text="Confirm"
							type={ButtonType.SOLID}
							variant={ButtonVariant.STANDARD}
							size={ButtonSize.SMALL}
							onClick={() => {
								handleConfirm();
							}}
						/>
					</ModalFooterRight>
				</ModalFooter>
			</Modal>
		</>
	);
};
